import { useEffect, useState } from "react";

import api from "../api/client";

/**
 * License management (admin). Surfaces the appliance's current license and
 * lets an admin activate a token, force a Hub check-in, or deactivate.
 * Backed by /api/v1/license/{status,activate,checkin,deactivate}.
 */

type LicenseStatus = {
  status: string;
  licensed: boolean;
  license_id?: string | null;
  customer_name?: string | null;
  tier?: string | null;
  caps?: { devices?: number | null; techs?: number | null; customers?: number | null };
  features?: string[];
  issued_at?: string | null;
  expires_at?: string | null;
  device_count?: number;
  device_cap_ok?: boolean;
  last_checkin_at?: string | null;
  last_checkin_status?: string | null;
  read_only?: boolean;
  locked?: boolean;
};

const STATUS_PILL: Record<string, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  grace: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  expired: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  revoked: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  unlicensed: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
};

export default function License() {
  const [lic, setLic] = useState<LicenseStatus | null>(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ hub_url: "", public_key: "", token: "" });
  const [showActivate, setShowActivate] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/api/v1/license/status");
      setLic(r.data);
    } catch (e: unknown) {
      setErr((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || "Failed to load license");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const detail = (e: unknown) =>
    (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;

  const activate = async () => {
    setErr("");
    setMsg("");
    setBusy(true);
    try {
      // Stash hub URL + public key first so the token verifies + check-ins work.
      const settings: Record<string, string> = {};
      // These are settable via the generic settings PUT (admin).
      if (form.hub_url) await api.put("/api/v1/settings/license_hub_url", { value: form.hub_url });
      if (form.public_key) await api.put("/api/v1/settings/license_hub_public_key", { value: form.public_key });
      void settings;
      const r = await api.post("/api/v1/license/activate", { token: form.token.trim() });
      setLic(r.data);
      setMsg(`Activated ${r.data.license_id} (${r.data.tier}).`);
      setShowActivate(false);
      setForm({ hub_url: "", public_key: "", token: "" });
      // Tell the app-wide license context to re-evaluate so the lock screen /
      // hidden nav clear immediately (instead of on its next 60s poll).
      window.dispatchEvent(new Event("license:refresh"));
    } catch (e: unknown) {
      setErr(detail(e) || "Activation failed");
    } finally {
      setBusy(false);
    }
  };

  const checkIn = async () => {
    setErr("");
    setMsg("");
    setBusy(true);
    try {
      const r = await api.post("/api/v1/license/checkin", {});
      setLic(r.data.state || r.data);
      setMsg(r.data.ok ? `Check-in: ${r.data.status}` : `Check-in: ${r.data.reason}`);
      window.dispatchEvent(new Event("license:refresh"));
    } catch (e: unknown) {
      setErr(detail(e) || "Check-in failed");
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    if (!window.confirm("Remove the local license? The appliance becomes unlicensed (no enforcement).")) return;
    setBusy(true);
    setErr("");
    try {
      await api.post("/api/v1/license/deactivate", {});
      setMsg("License removed.");
      load();
      window.dispatchEvent(new Event("license:refresh"));
    } catch (e: unknown) {
      setErr(detail(e) || "Deactivate failed");
    } finally {
      setBusy(false);
    }
  };

  const fmtCap = (n: number | null | undefined) => (n == null ? "Unlimited" : String(n));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">License</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Activation, status, and enforcement for this appliance. Tokens are verified
            offline against the Hub's public key; check-ins refresh the lease and pick up
            revocation.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowActivate((s) => !s)} className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium">
            {lic?.licensed ? "Re-activate / renew" : "Activate license"}
          </button>
        </div>
      </div>

      {err && <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">{err}</div>}
      {msg && <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300 px-4 py-2 rounded-lg text-sm">{msg}</div>}

      {lic && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_PILL[lic.status] || STATUS_PILL.unlicensed}`}>
                {lic.status}
              </span>
              {lic.read_only && <span className="text-xs text-amber-700 dark:text-amber-400">read-only (grace)</span>}
              {lic.locked && <span className="text-xs text-red-700 dark:text-red-400">locked</span>}
            </div>
            {lic.licensed && (
              <div className="flex gap-2">
                <button onClick={checkIn} disabled={busy} className="px-3 py-1.5 text-sm border dark:border-gray-600 dark:text-gray-200 rounded-lg disabled:opacity-50">
                  Check in now
                </button>
                <button onClick={deactivate} disabled={busy} className="px-3 py-1.5 text-sm text-red-700 dark:text-red-400 border border-red-300 dark:border-red-800 rounded-lg disabled:opacity-50">
                  Deactivate
                </button>
              </div>
            )}
          </div>

          {!lic.licensed ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This appliance is <strong>unlicensed</strong> — no enforcement is applied. Activate a
              token to enable license-based device caps + expiry.
            </p>
          ) : (
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <Item k="License ID" v={lic.license_id || "—"} />
              <Item k="Customer" v={lic.customer_name || "—"} />
              <Item k="Tier" v={lic.tier || "—"} />
              <Item
                k="Devices"
                v={`${lic.device_count ?? "?"} / ${fmtCap(lic.caps?.devices)}`}
                warn={lic.device_cap_ok === false}
              />
              <Item k="Tech cap" v={fmtCap(lic.caps?.techs)} />
              <Item k="Customer cap" v={fmtCap(lic.caps?.customers)} />
              <Item k="Issued" v={lic.issued_at?.slice(0, 10) || "—"} />
              <Item k="Expires" v={lic.expires_at?.slice(0, 10) || "Perpetual"} />
              <Item
                k="Last check-in"
                v={lic.last_checkin_at ? `${lic.last_checkin_at.slice(0, 16).replace("T", " ")} (${lic.last_checkin_status})` : "Never"}
              />
              <div className="col-span-2 md:col-span-3">
                <dt className="text-xs text-gray-500 dark:text-gray-400 mb-1">Features</dt>
                <dd className="flex flex-wrap gap-1">
                  {(lic.features || []).length === 0 ? (
                    <span className="text-gray-400 dark:text-gray-500">All (unrestricted)</span>
                  ) : (
                    (lic.features || []).map((f) => (
                      <span key={f} className="text-xs bg-gray-100 dark:bg-gray-700 dark:text-gray-200 px-2 py-0.5 rounded">{f}</span>
                    ))
                  )}
                </dd>
              </div>
            </dl>
          )}
        </div>
      )}

      {showActivate && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 space-y-3">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Activate license</h2>
          <label className="block text-sm">
            <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">License Hub URL (optional)</span>
            <input
              value={form.hub_url}
              onChange={(e) => setForm({ ...form, hub_url: e.target.value })}
              placeholder="https://license.example.com"
              className="w-full border dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 dark:text-gray-100"
            />
          </label>
          <label className="block text-sm">
            <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Hub public key (base64, optional)</span>
            <input
              value={form.public_key}
              onChange={(e) => setForm({ ...form, public_key: e.target.value })}
              className="w-full border dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 dark:text-gray-100 font-mono text-xs"
            />
          </label>
          <label className="block text-sm">
            <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Activation token</span>
            <textarea
              value={form.token}
              onChange={(e) => setForm({ ...form, token: e.target.value })}
              rows={3}
              placeholder="v4.public...."
              className="w-full border dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 dark:text-gray-100 font-mono text-xs"
            />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowActivate(false)} className="px-4 py-2 text-sm border dark:border-gray-600 dark:text-gray-200 rounded-lg">Cancel</button>
            <button onClick={activate} disabled={busy || !form.token.trim()} className="px-4 py-2 text-sm bg-brand text-white rounded-lg disabled:opacity-50">
              {busy ? "Activating…" : "Activate"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Item({ k, v, warn }: { k: string; v: string; warn?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-gray-500 dark:text-gray-400">{k}</dt>
      <dd className={`font-medium ${warn ? "text-red-700 dark:text-red-400" : "text-gray-900 dark:text-gray-100"}`}>{v}</dd>
    </div>
  );
}
